<template>
  <div class="counter-widget" :style="widgetStyles">
    <div v-if="widget.title" class="counter-title mb-4 text-center">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>

    <div class="counters-container" :class="containerClass">
      <div 
        v-for="(counter, index) in widget.counters" 
        :key="counter.id"
        class="counter-item"
      >
        <div class="counter-card h-100 text-center p-4" :style="counterCardStyles(counter)">
          <!-- Icon -->
          <div v-if="counter.icon" class="counter-icon mb-3">
            <i :class="counter.icon" :style="iconStyles(counter)"></i>
          </div>

          <!-- Number -->
          <div class="counter-number mb-2">
            <span v-if="counter.prefix" class="counter-prefix me-1">{{ counter.prefix }}</span>
            <span class="counter-value" :style="numberStyles(counter)">
              {{ animatedNumbers[index] }}
            </span>
            <span v-if="counter.suffix" class="counter-suffix ms-1">{{ counter.suffix }}</span>
          </div>

          <!-- Label -->
          <div class="counter-label">
            <span :style="labelStyles(counter)">{{ counter.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { CounterWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'

interface Props {
  widget: CounterWidget
}

const props = defineProps<Props>()

// Animation state
const animatedNumbers = ref<number[]>([])

// Initialize animated numbers
const initializeNumbers = () => {
  animatedNumbers.value = props.widget.counters.map(() => 0)
}

// Animate counters
const animateCounters = () => {
  if (props.widget.animated === false) {
    animatedNumbers.value = props.widget.counters.map(c => c.number)
    return
  }

  const duration = props.widget.duration || 2000
  const steps = 60
  const stepDuration = duration / steps

  props.widget.counters.forEach((counter, index) => {
    const increment = counter.number / steps
    let currentStep = 0

    const interval = setInterval(() => {
      if (currentStep >= steps) {
        animatedNumbers.value[index] = counter.number
        clearInterval(interval)
      } else {
        currentStep++
        animatedNumbers.value[index] = Math.floor(increment * currentStep)
      }
    }, stepDuration)
  })
}

onMounted(() => {
  initializeNumbers()
  animateCounters()
})

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '2rem 0',
  ...resolveWidgetBackgroundStyle(props.widget),
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || '#333',
  fontSize: props.widget.titleSize || '2rem',
  margin: '0'
}))

const containerClass = computed(() => {
  const columns = props.widget.columns || 4
  return `row row-cols-1 row-cols-md-${Math.min(columns, 4)} g-4`
})

const counterCardStyles = (counter: any) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '10px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease',
  border: `2px solid ${counter.color || '#007bff'}20`
})

const iconStyles = (counter: any) => ({
  fontSize: '3rem',
  color: counter.color || '#007bff'
})

const numberStyles = (counter: any) => ({
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: counter.color || '#007bff',
  display: 'inline-block'
})

const labelStyles = (counter: any) => ({
  fontSize: '1.1rem',
  color: '#6c757d',
  textTransform: 'uppercase' as const,
  fontWeight: '500',
  letterSpacing: '1px'
})
</script>

<style scoped>
.counter-widget {
  width: 100%;
}

.counter-item {
  margin-bottom: 1rem;
}

.counter-card {
  cursor: default;
  position: relative;
  overflow: hidden;
}

.counter-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.15) !important;
}

.counter-icon {
  animation: iconBounce 2s infinite ease-in-out;
}

@keyframes iconBounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.counter-number {
  font-family: 'Arial', sans-serif;
  line-height: 1;
}

.counter-value {
  transition: all 0.3s ease;
}

.counter-label {
  opacity: 0.8;
}

.counter-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, transparent, currentColor, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.counter-card:hover::before {
  opacity: 0.3;
}
</style>
